import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import useStyles from './styles';
import ContentLabel from '../../atoms/ContentLabel/ContentLabel';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { PostType, SubmittedInfo } from 'types/post';
import { PostCategory } from 'types/category';
import { DOMAIN_BASE, ROUTES } from 'config/Nav';
import {
  Link,
  formatDate,
  ShareButtonGroup,
  ShareLinkOptions,
  PostAuthor,
  Avatar,
} from '@elevator/ui';
import { Skeleton } from '@mui/material';
import { useRouter } from 'next/router';
import ShareVariant from 'mdi-material-ui/ShareVariant';

/**
 * PostHeader Component
 *
 * Displays post header information including title, description, category, publication date,
 * author information, and social sharing buttons. Used in the Post organism to render
 * the header section of a post page.
 *
 * Features:
 * - Displays post title, description, category, and publication date
 * - Shows author avatar, name, and role
 * - Includes social sharing buttons
 * - Supports Featured post type with special centered styling
 * - Shows loading skeletons during data fetching
 * - Displays post type badge (with optional sponsored by text)
 *
 * @param props - Component props
 * @param props.title - Post title
 * @param props.description - Post description/excerpt
 * @param props.slug - Post URL slug
 * @param props.type - Post type (Standard, Featured, Premiere, etc.)
 * @param props.publishedAt - Publication timestamp
 * @param props.authors - Array of post authors (uses first author for display)
 * @param props.categories - Array of post categories (uses first category for display)
 * @param props.shareGroupProps - Optional props for ShareButtonGroup component
 * @param props.submission - Submission information (includes sponsoredBy if applicable)
 * @param props.isLoading - Whether the post data is currently loading
 */
const PostHeader = (props: PostHeaderPropTypes) => {
  const { classes } = useStyles();

  const {
    title,
    description,
    slug,
    type,
    publishedAt,
    authors,
    categories,
    shareGroupProps,
    submission,
    isLoading,
  } = props;

  const router = useRouter();

  // Get first author (required for display)
  const author = authors?.[0];
  const isFeature = type === 'Featured';

  // Determine if skeleton should be shown
  const showSkeleton = useMemo(() => {
    return router.isFallback || isLoading;
  }, [router.isFallback, isLoading]);

  // Get first category if available
  const firstCategory = categories?.[0];

  // Popover state for share button
  const [shareAnchorEl, setShareAnchorEl] = useState<HTMLElement | null>(null);
  const sharePopoverOpen = Boolean(shareAnchorEl);

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  /**
   * Renders post title, description, category, date, and post type badge.
   * Handles loading skeleton state and Featured post special styling.
   */
  const renderPostTitleInfo = () => {
    return (
      <Stack
        direction="column"
        spacing={1}
        useFlexGap
        className={clsx(classes.postMetaContainer, {
          [classes.featuredPostMetaContainer]: isFeature,
        })}
      >
        <Stack
          direction="column"
          spacing={3}
          useFlexGap
          alignItems={isFeature ? 'center' : 'flex-start'}
          className={clsx(classes.spacedRow, {
            [classes.featureMetaStack]: isFeature,
          })}
        >
          {type !== 'Standard' && (
            <Box className={classes.contentLabelWrapper}>
              {submission?.sponsoredBy ? (
                <ContentLabel type={type} sponsoredBy={submission.sponsoredBy} />
              ) : (
                <ContentLabel type={type} />
              )}
            </Box>
          )}
          <Box className={classes.categoryWrapper}>
            {showSkeleton ? (
              <Skeleton animation="pulse" variant="rectangular" width={150} height={16} />
            ) : (
              firstCategory && (
                <Link href={`${ROUTES.CATEGORY.to}${firstCategory.slug}`}>
                  <Typography variant="overline1" className={classes.categoryLink}>
                    {firstCategory.name}
                  </Typography>
                </Link>
              )
            )}
          </Box>
        </Stack>
        <Stack
          direction="column"
          spacing={1}
          useFlexGap
          className={classes.postMetaTitleSubContainer}
        >
          {showSkeleton ? (
            <Skeleton animation="pulse" variant="rectangular" width={300} height={40} />
          ) : (
            <Link href={ROUTES.POST.fileApiPath} as={`/${slug}`}>
              <Typography variant="h3" className={classes.titleLink}>
                {title}
              </Typography>
            </Link>
          )}
          {showSkeleton ? (
            <Skeleton animation="pulse" variant="rectangular" width={500} height={55} />
          ) : (
            <Typography variant="body1" color="textSecondary" className={classes.italics}>
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  };

  /**
   * Renders author information (avatar, name, role) and social sharing buttons.
   * Handles loading skeleton state and missing author data gracefully.
   */
  const renderPostAuthorRow = () => {
    const shareButton = (
      <>
        <IconButton
          onClick={handleShareClick}
          aria-label="Share post"
          aria-describedby={sharePopoverOpen ? 'share-popover' : undefined}
          size="small"
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            flexShrink: 0,
            '& svg': {
              fill: 'url(#share-gradient)',
            },
          }}
        >
          <Box
            component="svg"
            sx={{ position: 'absolute', width: 0, height: 0 }}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="share-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F5EC0F" />
                <stop offset="100%" stopColor="#DD09FF" />
              </linearGradient>
            </defs>
          </Box>
          <ShareVariant />
        </IconButton>
        <Popover
          id="share-popover"
          open={sharePopoverOpen}
          anchorEl={shareAnchorEl}
          onClose={handleShareClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 1 }}>
            <ShareButtonGroup
              shareLink={`${DOMAIN_BASE}/${slug}`}
              size="small"
              postTitle={title}
              {...shareGroupProps}
            />
          </Box>
        </Popover>
      </>
    );

    if (!author && !showSkeleton) {
      // If no author and not loading, don't render author section
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          className={classes.spacedRow}
        >
          <Box />
          {shareButton}
        </Stack>
      );
    }

    return (
      <Stack direction="row" justifyContent="space-between" className={classes.spacedRow}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          alignItems="center"
          className={classes.authorContainer}
        >
          {showSkeleton ? (
            <Skeleton animation="pulse" variant="rectangular" height={32} width={32} />
          ) : (
            author && (
              <Avatar
                size="small"
                src={author.avatar?.sourceUrl}
                srcSet={author.avatar?.sourceSet}
                alt={author.displayName || author.preferredUsername}
                username={author.displayName || author.preferredUsername}
              />
            )
          )}
          <Box>
            {showSkeleton ? (
              <>
                <Skeleton animation="pulse" variant="text" width={50} height={18} />
                <Skeleton animation="pulse" variant="text" width={55} height={22} />
              </>
            ) : (
              author && (
                <>
                  <Typography display="block" color="textSecondary" variant="subtitle2">
                    {author.role}
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Link href={`${ROUTES.AUTHORS.to}${author.preferredUsername}`} inline>
                      <Typography variant="subtitle2" className={classes.authorLink}>
                        {author.displayName || author.preferredUsername}
                      </Typography>
                    </Link>
                    <Typography variant="overline2" color="textSecondary">
                      {formatDate(parseInt(String(publishedAt), 10), {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Stack>
                </>
              )
            )}
          </Box>
        </Stack>
        {shareButton}
      </Stack>
    );
  };

  return (
    <Stack
      direction="column"
      spacing={2}
      useFlexGap
      alignItems="flex-start"
      className={classes.root}
      data-component="PostHeader"
    >
      {renderPostTitleInfo()}
      {renderPostAuthorRow()}
    </Stack>
  );
};

export interface PostHeaderPropTypes {
  /** Post title */
  title: string;
  /** Post description/excerpt */
  description: string;
  /** Post URL slug */
  slug: string;
  /** Post type (Standard, Featured, Premiere, etc.) */
  type: PostType;
  /** Publication timestamp */
  publishedAt: string | Date;
  /** Array of post authors (first author is displayed) */
  authors: Array<PostAuthor>;
  /** Array of post categories (first category is displayed) */
  categories: Array<PostCategory>;
  /** Optional props for ShareButtonGroup component */
  shareGroupProps?: ShareLinkOptions;
  /** Submission information (includes sponsoredBy if applicable) */
  submission: SubmittedInfo;
  /** Whether the post data is currently loading */
  isLoading: boolean;
}

export default PostHeader;
